/**
 * 客服中心页面
 *
 * 4 个 tab:
 *   - FAQ 常见问题(分类筛选)
 *   - 我的工单(列表 + 创建)
 *   - 工单详情(消息 + 回复)
 *   - 联系客服(多种方式)
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAutoSave } from '../hooks/useAutoSave.js';
import {
  HelpCircle, MessageSquare, Plus, ChevronRight, Search, Send, CheckCircle2,
  Clock, XCircle, ArrowLeft, Star, StarOff, Phone, Mail, MessageCircle,
  Filter, Loader2, ChevronDown, ThumbsUp, Globe
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../lib/api.js';
import { useUser } from '../stores/user.js';
import PageHeader from '../components/PageHeader.jsx';

const CATEGORIES = ['全部', '产品', '会员', '推荐', '支付', '技术', '退款', '其他'];
const PRIORITY_LABELS = { low: '低', normal: '普通', high: '高', urgent: '紧急' };
const STATUS_LABELS = { open: '待处理', pending: '已回复', resolved: '已解决', closed: '已关闭' };
const STATUS_COLORS = {
  open: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  pending: 'text-brand-cyan bg-brand-cyan/10 border-brand-cyan/30',
  resolved: 'text-brand-gold bg-brand-gold/10 border-brand-gold/30',
  closed: 'text-ink-3 bg-white/5 border-white/10',
};

export default function SupportPage() {
  const { user } = useUser();
  const [tab, setTab] = useState('faq');
  const [selectedTicket, setSelectedTicket] = useState(null);

  return (
    <div className="space-y-3 pb-24">
      <PageHeader
        title="客服中心"
        subtitle="7×24 在线服务 · 提交工单 · 常见问题自助查询"
        icon={HelpCircle}
      />

      {/* Tab */}
      <div className="card !p-2">
        <div className="grid grid-cols-3 gap-1">
          {[
            { id: 'faq', label: '常见问题', icon: HelpCircle },
            { id: 'tickets', label: '我的工单', icon: MessageSquare },
            { id: 'contact', label: '联系我们', icon: Phone },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setSelectedTicket(null); }}
              className={clsx(
                'rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1.5 transition',
                tab === t.id
                  ? 'bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/30'
                  : 'text-ink-2 hover:bg-white/5'
              )}
            >
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {tab === 'faq' && <FaqTab key="faq" />}
        {tab === 'tickets' && (
          selectedTicket
            ? <TicketDetailTab key="detail" ticketId={selectedTicket} onBack={() => setSelectedTicket(null)} />
            : <MyTicketsTab key="list" onSelect={setSelectedTicket} onCreate={() => setTab('new')} />
        )}
        {tab === 'new' && <NewTicketTab key="new" onBack={() => setTab('tickets')} onSuccess={(id) => { setTab('tickets'); setSelectedTicket(id); }} />}
        {tab === 'contact' && (
          <ContactTab
            key="contact"
            onStartChat={() => {
              // 切到工单 tab,如果有 open 状态的工单就跳过去,否则建一个新的"在线客服"工单
              setTab('tickets');
              setSelectedTicket(null);
              setTimeout(() => {
                // 滚动到顶部
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }, 100);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Tab 1: FAQ ──
function FaqTab() {
  const [category, setCategory] = useState('全部');
  const [search, setSearch] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [categories, setCategories] = useState(['全部']);

  useEffect(() => {
    load();
  }, [category]);

  const load = async () => {
    setLoading(true);
    try {
      const cat = category === '全部' ? 'all' : category;
      const res = await api.support.faqs(cat);
      if (res?.ok) {
        setFaqs(res.faqs || []);
        if (res.categories) setCategories(['全部', ...res.categories]);
      }
    } catch (e) {
      // 离线兜底
      setFaqs(OFFLINE_FAQS.filter(f => category === '全部' || f.category === category));
    }
    setLoading(false);
  };

  const handleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
    if (expanded !== id) {
      api.support.faqView(id).catch(() => {});
    }
  };

  const handleHelpful = async (id, e) => {
    e.stopPropagation();
    try {
      await api.support.faqHelpful(id);
      toast.success('感谢反馈!');
    } catch {}
  };

  const filtered = useMemo(() => {
    if (!search) return faqs;
    const kw = search.toLowerCase();
    return faqs.filter(f =>
      f.question.toLowerCase().includes(kw) ||
      f.answer.toLowerCase().includes(kw)
    );
  }, [faqs, search]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-3"
    >
      <div className="card !p-3">
        <div className="flex items-center gap-2 mb-2">
          <Search size={14} className="text-ink-3" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜索问题..."
            className="flex-1 bg-transparent outline-none text-sm"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={clsx(
                'px-2.5 py-1 rounded-full text-xs whitespace-nowrap transition',
                category === c
                  ? 'bg-brand-cyan/30 text-brand-cyan font-semibold'
                  : 'bg-white/5 text-ink-2 hover:bg-white/10'
              )}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card text-center py-8 text-ink-2 text-sm">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
          加载中...
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-8 text-ink-3 text-sm">
          没有匹配的问题
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(f => (
            <div key={f.id} className="card !p-0 overflow-hidden">
              <button
                onClick={() => handleExpand(f.id)}
                className="w-full p-3 flex items-center gap-2 text-left hover:bg-white/5 transition"
              >
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-ink-2 flex-shrink-0">
                  {f.category}
                </span>
                <span className="text-sm font-semibold flex-1">{f.question}</span>
                <ChevronDown
                  size={16}
                  className={clsx('text-ink-3 transition-transform', expanded === f.id && 'rotate-180')}
                />
              </button>
              <AnimatePresence>
                {expanded === f.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden border-t border-white/5"
                  >
                    <div className="p-3 text-sm text-ink-1 leading-relaxed whitespace-pre-wrap">
                      {f.answer}
                    </div>
                    <div className="px-3 pb-3 flex items-center justify-between text-xs text-ink-3">
                      <span>👀 {f.view_count || 0} 次浏览</span>
                      <button
                        onClick={(e) => handleHelpful(f.id, e)}
                        className="flex items-center gap-1 text-brand-cyan hover:underline"
                      >
                        <ThumbsUp size={12} />
                        有帮助 ({f.helpful_count || 0})
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ── Tab 2: 我的工单 ──
function MyTicketsTab({ onSelect, onCreate }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.support.tickets();
      if (res?.ok) setTickets(res.tickets || []);
    } catch (e) {}
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-3"
    >
      <button
        onClick={onCreate}
        className="card w-full hover:border-brand-cyan/40 transition flex items-center justify-center gap-2 py-3"
      >
        <Plus size={16} className="text-brand-cyan" />
        <span className="font-semibold text-sm text-brand-cyan">新建工单</span>
      </button>

      {loading ? (
        <div className="card text-center py-8 text-ink-2 text-sm">
          <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
          加载中...
        </div>
      ) : tickets.length === 0 ? (
        <div className="card text-center py-8 text-ink-3 text-sm">
          <MessageSquare size={32} className="mx-auto mb-2 opacity-30" />
          还没有工单
          <div className="text-xs mt-1">遇到问题?点上方"新建工单"</div>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => (
            <button
              key={t.id}
              onClick={() => onSelect(t.id)}
              className="card w-full text-left hover:border-brand-cyan/30 transition"
            >
              <div className="flex items-start gap-2 mb-1.5">
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-ink-2 flex-shrink-0">
                  {t.category}
                </span>
                <span className={clsx(
                  'text-[10px] px-1.5 py-0.5 rounded border flex-shrink-0',
                  STATUS_COLORS[t.status]
                )}>
                  {STATUS_LABELS[t.status]}
                </span>
                {t.priority === 'urgent' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-accent-danger/20 text-accent-danger flex-shrink-0">
                    紧急
                  </span>
                )}
              </div>
              <div className="text-sm font-semibold mb-1 line-clamp-1">{t.subject}</div>
              <div className="text-xs text-ink-2 line-clamp-1">
                {t.last_message || '(暂无回复)'}
              </div>
              <div className="flex items-center justify-between mt-2 text-[10px] text-ink-3">
                <span>#{t.id}</span>
                <span>{fmtTime(t.last_reply_at || t.created_at)} · {t.message_count || 0} 条消息</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// ── Tab 3: 工单详情 ──
function TicketDetailTab({ ticketId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');

  useEffect(() => {
    load();
    // 每 5 秒自动拉新消息(轮询)
    const timer = setInterval(() => {
      load(true);  // silent = true
    }, 5000);
    return () => clearInterval(timer);
  }, [ticketId]);

  const lastMsgCount = useRef(0);
  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.support.ticketDetail(ticketId);
      if (res?.ok) {
        setData(res);
        // 新消息提示
        if (silent && res.messages && res.messages.length > lastMsgCount.current) {
          const newMsgs = res.messages.length - lastMsgCount.current;
          const latest = res.messages[res.messages.length - 1];
          if (latest?.from_role === 'admin') {
            toast.success(`客服回复了 ${newMsgs} 条消息`);
          }
        }
        lastMsgCount.current = res.messages?.length || 0;
      }
    } catch (e) {}
    if (!silent) setLoading(false);
  };

  const handleReply = async () => {
    if (!reply.trim()) {
      toast.error('请输入内容');
      return;
    }
    setSending(true);
    try {
      const res = await api.support.ticketReply(ticketId, reply);
      if (res?.ok) {
        setReply('');
        toast.success('已发送');
        load();
      } else {
        toast.error(res?.error || '发送失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSending(false);
  };

  const handleClose = async () => {
    if (!confirm('确认关闭工单?关闭后无法再回复。')) return;
    try {
      const res = await api.support.ticketClose(ticketId);
      if (res?.ok) {
        toast.success('已关闭');
        load();
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleRate = async () => {
    try {
      const res = await api.support.ticketRate(ticketId, rating, comment);
      if (res?.ok) {
        toast.success('已评价,谢谢反馈!');
        setShowRate(false);
        load();
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) {
    return (
      <div className="card text-center py-8 text-ink-2 text-sm">
        <Loader2 size={24} className="mx-auto mb-2 animate-spin" />
        加载中...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card text-center py-8 text-ink-3 text-sm">
        工单不存在或无权访问
        <button onClick={onBack} className="block mx-auto mt-3 btn-secondary text-xs">返回</button>
      </div>
    );
  }

  const { ticket, messages = [], rating: existingRating } = data;
  const isClosed = ticket.status === 'closed';

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-3"
    >
      <div className="card">
        <button onClick={onBack} className="text-xs text-ink-3 hover:text-ink-0 mb-2 flex items-center gap-1">
          <ArrowLeft size={12} />
          返回列表
        </button>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-ink-2">
            {ticket.category}
          </span>
          <span className={clsx(
            'text-[10px] px-1.5 py-0.5 rounded border',
            STATUS_COLORS[ticket.status]
          )}>
            {STATUS_LABELS[ticket.status]}
          </span>
          <span className="text-[10px] text-ink-3">#{ticket.id}</span>
        </div>
        <h2 className="text-base font-bold mb-1">{ticket.subject}</h2>
        <div className="text-xs text-ink-3">{fmtTime(ticket.created_at)} 提交</div>
      </div>

      {/* 消息列表 */}
      <div className="space-y-2">
        {messages.map(m => (
          <div
            key={m.id}
            className={clsx(
              'card !p-3',
              m.sender_type === 'admin'
                ? 'border-brand-cyan/30 bg-brand-cyan/5'
                : 'border-white/10'
            )}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <div className={clsx(
                'w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold',
                m.sender_type === 'admin' ? 'bg-brand-cyan text-bg-base' : 'bg-brand-purple text-bg-base'
              )}>
                {m.sender_type === 'admin' ? '客' : '我'}
              </div>
              <span className="text-xs font-semibold">
                {m.sender_type === 'admin' ? '客服' : '我'}
              </span>
              <span className="text-[10px] text-ink-3">{fmtTime(m.created_at)}</span>
            </div>
            <div className="text-sm text-ink-1 whitespace-pre-wrap leading-relaxed">
              {m.content}
            </div>
          </div>
        ))}
      </div>

      {/* 评价 */}
      {!existingRating && ticket.status !== 'closed' && (
        <button
          onClick={() => setShowRate(true)}
          className="card w-full text-center text-xs text-brand-cyan hover:border-brand-cyan/40 transition"
        >
          <Star size={14} className="inline mr-1" />
          评价本次服务
        </button>
      )}
      {existingRating && (
        <div className="card text-center text-xs text-ink-2">
          <div className="text-ink-3 mb-1">你的评价</div>
          <div className="flex justify-center gap-1">
            {[1, 2, 3, 4, 5].map(i => (
              <Star
                key={i}
                size={16}
                className={i <= existingRating.rating ? 'text-brand-gold fill-brand-gold' : 'text-ink-3'}
              />
            ))}
          </div>
          {existingRating.comment && (
            <div className="text-ink-2 mt-1.5 text-xs">{existingRating.comment}</div>
          )}
        </div>
      )}

      {/* 回复输入 */}
      {!isClosed && (
        <div className="card !p-3">
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="输入回复..."
            rows={3}
            maxLength={5000}
            className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-purple/40 resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-ink-3">{reply.length}/5000</span>
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="text-xs text-ink-3 hover:text-accent-danger"
              >
                关闭工单
              </button>
              <button
                onClick={handleReply}
                disabled={!reply.trim() || sending}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5',
                  reply.trim() && !sending
                    ? 'btn-primary'
                    : 'bg-white/5 text-ink-3 cursor-not-allowed'
                )}
              >
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                发送
              </button>
            </div>
          </div>
        </div>
      )}

      {isClosed && (
        <div className="card text-center text-xs text-ink-2 py-3">
          <XCircle size={20} className="mx-auto mb-1 opacity-50" />
          工单已关闭
        </div>
      )}

      {/* 评价弹窗 */}
      <AnimatePresence>
        {showRate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowRate(false)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-sm glass-strong rounded-2xl p-5"
            >
              <h3 className="text-base font-bold text-center mb-3">评价本次服务</h3>
              <div className="flex justify-center gap-2 mb-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <button key={i} onClick={() => setRating(i)}>
                    <Star
                      size={32}
                      className={i <= rating ? 'text-brand-gold fill-brand-gold' : 'text-ink-3'}
                    />
                  </button>
                ))}
              </div>
              <textarea
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="说点什么...(可选)"
                rows={3}
                maxLength={500}
                className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-purple/40 resize-none"
              />
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button onClick={() => setShowRate(false)} className="btn-secondary text-sm">取消</button>
                <button onClick={handleRate} className="btn-primary text-sm">提交</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Tab 4: 新建工单 ──
function NewTicketTab({ onBack, onSuccess }) {
  const [category, setCategory, catCtrl] = useAutoSave('new_ticket_category', '产品');
  const [subject, setSubject, subjCtrl] = useAutoSave('new_ticket_subject', '');
  const [content, setContent, contentCtrl] = useAutoSave('new_ticket_content', '');
  const [priority, setPriority, prioCtrl] = useAutoSave('new_ticket_priority', 'normal');
  const [submitting, setSubmitting] = useState(false);

  const hasDraft = subject || content;
  const clearDraft = () => {
    if (confirm('确认清空草稿?')) {
      subjCtrl.reset();
      contentCtrl.reset();
      prioCtrl.reset();
      catCtrl.reset();
      toast.success('草稿已清空');
    }
  };

  const handleSubmit = async () => {
    if (!subject.trim()) {
      toast.error('请输入主题');
      return;
    }
    if (!content.trim()) {
      toast.error('请输入问题详情');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.support.createTicket({ category, subject, content, priority });
      if (res?.ok) {
        toast.success('工单已创建,客服会尽快回复');
        // 清空草稿
        subjCtrl.reset();
        contentCtrl.reset();
        prioCtrl.reset();
        catCtrl.reset();
        onSuccess(res.id);
      } else {
        toast.error(res?.error || '创建失败');
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSubmitting(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-3"
    >
      <div className="card">
        <button onClick={onBack} className="text-xs text-ink-3 hover:text-ink-0 mb-3 flex items-center gap-1">
          <ArrowLeft size={12} />
          返回
        </button>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold">新建工单</h2>
          {hasDraft && (
            <button
              onClick={clearDraft}
              className="text-[10px] text-ink-3 hover:text-accent-danger flex items-center gap-1"
            >
              🗑 清空草稿
            </button>
          )}
        </div>
        {hasDraft && (
          <div className="rounded-lg bg-brand-cyan/5 border border-brand-cyan/20 p-2 text-[10px] text-brand-cyan mb-3">
            💾 已自动恢复草稿(填到一半刷新不丢)
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs text-ink-2 mb-1.5 block">问题分类</label>
            <div className="grid grid-cols-4 gap-1.5">
              {CATEGORIES.filter(c => c !== '全部').map(c => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={clsx(
                    'py-1.5 rounded-lg text-xs transition',
                    category === c
                      ? 'bg-brand-cyan/30 text-brand-cyan font-semibold border border-brand-cyan/30'
                      : 'bg-white/5 text-ink-2'
                  )}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-ink-2 mb-1.5 block">优先级</label>
            <div className="grid grid-cols-4 gap-1.5">
              {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => setPriority(k)}
                  className={clsx(
                    'py-1.5 rounded-lg text-xs transition',
                    priority === k
                      ? 'bg-brand-cyan/30 text-brand-cyan font-semibold border border-brand-cyan/30'
                      : 'bg-white/5 text-ink-2'
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-ink-2 mb-1.5 block">主题(≤200 字)</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              maxLength={200}
              placeholder="一句话描述问题"
              className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-purple/40"
            />
            <div className="text-[10px] text-ink-3 mt-0.5 text-right">{subject.length}/200</div>
          </div>

          <div>
            <label className="text-xs text-ink-2 mb-1.5 block">详细描述(≤5000 字)</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              maxLength={5000}
              rows={6}
              placeholder="请详细描述问题,包括:\n- 复现步骤\n- 预期 vs 实际\n- 截图(如能提供)"
              className="w-full bg-bg-card border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-brand-purple/40 resize-none"
            />
            <div className="text-[10px] text-ink-3 mt-0.5 text-right">{content.length}/5000</div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={clsx(
              'w-full py-3 rounded-lg font-semibold text-sm flex items-center justify-center gap-2',
              !submitting ? 'btn-primary' : 'bg-white/5 text-ink-3 cursor-not-allowed'
            )}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            提交工单
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Tab 5: 联系我们 ──
function ContactTab({ onStartChat }) {
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.publicSettings().then(r => setSettings(r.settings || {})).catch(() => {});
  }, []);

  // 判断 value 是不是图片(data URL 或 http URL)
  const isImage = (v) => v && (v.startsWith('data:image') || v.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i));

  // 动态构建联系方式列表(从 settings 来,顺序固定)
  const contacts = [];
  if (settings.wechat_group_label) {
    contacts.push({
      icon: MessageCircle,
      title: settings.wechat_group_label,
      desc: settings.wechat_group_desc || '扫码加入用户群',
      qr: settings.wechat_group_qr || '👥',
      copyText: settings.wechat_group_id || settings.wechat_group_desc || '微信群',
      color: 'text-brand-cyan',
    });
  }
  if (settings.business_label || settings.business_email) {
    contacts.push({
      icon: Phone,
      title: settings.business_label || '商务合作',
      desc: settings.business_email || 'bd@3dmax.com',
      qr: settings.business_qr || '🤝',
      copyText: settings.business_email || 'bd@3dmax.com',
      color: 'text-brand-gold',
    });
  }
  if (settings.official_account_label) {
    contacts.push({
      icon: Globe,
      title: settings.official_account_label,
      desc: settings.official_account_id || '关注公众号',
      qr: settings.official_account_qr || '📢',
      copyText: settings.official_account_id || settings.official_account_label,
      color: 'text-brand-purple',
    });
  }
  if (settings.video_account_label) {
    contacts.push({
      icon: Globe,
      title: settings.video_account_label,
      desc: settings.video_account_id || '微信视频号',
      qr: '🎬',
      copyText: settings.video_account_id || settings.video_account_label,
      color: 'text-brand-rose',
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="space-y-3"
    >
      {/* 在线客服 — 主入口 */}
      <button
        onClick={onStartChat}
        className="card w-full text-left hover:border-brand-cyan/40 transition relative overflow-hidden group"
        style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(168,85,247,0.1) 100%)' }}
      >
        <div className="absolute top-0 right-0 text-7xl opacity-10 group-hover:scale-110 transition select-none pointer-events-none">💬</div>
        <div className="flex items-center gap-3 relative">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-cyan to-brand-purple flex items-center justify-center flex-shrink-0">
            <MessageCircle size={28} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="text-base font-bold flex items-center gap-2">
              在线客服
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400 font-semibold flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                在线
              </span>
            </div>
            <div className="text-xs text-ink-2 mt-1">直接对话,响应更快 · 一对一解答</div>
          </div>
          <ChevronRight size={20} className="text-brand-cyan group-hover:translate-x-1 transition" />
        </div>
      </button>

      {/* 其他联系方式 */}
      <div className="card">
        <h2 className="text-base font-bold mb-3 flex items-center gap-2">
          <Phone size={16} className="text-brand-cyan" />
          其他联系方式
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {contacts.map((c, i) => (
            <div key={i} className="rounded-xl bg-white/5 p-3 flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                {isImage(c.qr) ? (
                  <img src={c.qr} alt={c.title} className="w-full h-full object-contain" />
                ) : (
                  <div className="text-2xl">{c.qr}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className={clsx('text-sm font-bold truncate', c.color)}>{c.title}</div>
                <div className="text-xs text-ink-2 mt-0.5 truncate">{c.desc}</div>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(c.copyText);
                  toast.success('已复制');
                }}
                className="text-xs text-brand-cyan flex-shrink-0"
              >
                复制
              </button>
            </div>
          ))}
        </div>
        {settings.support_email && (
          <div className="mt-3 pt-3 border-t border-white/5 text-[10px] text-ink-3 space-y-0.5">
            <div>📧 客服邮箱: <span className="text-ink-1 font-mono">{settings.support_email}</span></div>
            {settings.support_phone && <div>📞 客服电话: <span className="text-ink-1 font-mono">{settings.support_phone}</span></div>}
            {settings.support_work_hours && <div>🕐 工作时间: <span className="text-ink-1">{settings.support_work_hours}</span></div>}
          </div>
        )}
      </div>

      <div className="card">
        <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
          <Clock size={14} className="text-brand-cyan" />
          服务时间
        </h3>
        <div className="text-xs text-ink-2 space-y-1">
          <div>📅 在线客服:7×24 全天(工作时间秒回,非工作日 2 小时内)</div>
          <div>🕐 人工服务:周一至周日 9:00 - 22:00</div>
          <div>⚡ 紧急问题:工单标"紧急"即优先处理</div>
          <div>💬 微信群:工作日有客服值班</div>
        </div>
      </div>
    </motion.div>
  );
}

// ── 工具函数 ──
function fmtTime(ts) {
  if (!ts) return '-';
  const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return '刚刚';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} 分钟前`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} 小时前`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} 天前`;
  return d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
}

// 离线兜底 FAQ
const OFFLINE_FAQS = [
  { id: -1, category: '产品', question: '什么是 3D MAX?', answer: '3D MAX 是基于 ML 的多层大底池选号系统...', view_count: 0, helpful_count: 0 },
  { id: -2, category: '会员', question: 'Pro 月付和年付有什么区别?', answer: '月付 ¥169 / 月,年付 ¥699 / 年(省 70%)。', view_count: 0, helpful_count: 0 },
];
